import CurrentUser from 'models/currentuser';
import TaskType, { SystemTaskTypes } from 'models/tasks/taskType';
import UserLanguage from 'models/userLanguage';
import { apiGetTaskTypes } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { EntityCache, ICache } from './entityCache';

export class TaskTypeCache extends EntityCache<TaskType> implements ICache<TaskType, TaskTypeCache> {
  private isLoading: boolean = false;

  private taskTypePromise: Promise<TaskType[]> | undefined;

  constructor() {
    super();
    this.getId = (tasktype) => tasktype.taskTypeId;
    this.getEmptyItem = () => new TaskType();
  }

  getSystem(systemType: SystemTaskTypes): TaskType | undefined {
    const taskType = this.items.find((i) => i.systemTaskType === systemType);
    if (taskType) {
      return taskType;
    } else {
      return undefined;
    }
  }

  // "The" form of a system role is what the server picks for it: the head of the lowest visible,
  // non-archived family of that role (SqlServerTaskStore.GetTaskTypeForApprovalAsync). This cache
  // holds heads only and the login read includes hidden forms, so the same rule here is the visible
  // form with the lowest family id. The cache order is not stable across forks (a new head gets a
  // higher id), so the first match could swap families after a form edit.
  getTypeOfSystemType(systemTaskType: SystemTaskTypes): TaskType | undefined {
    const candidates = this.items.filter((i) => i.systemTaskType === systemTaskType && !i.isHidden);
    if (candidates.length === 0) {
      return undefined;
    }

    const familyRank = (taskType: TaskType): number => taskType.taskTypeFamilyId ?? Number.MAX_SAFE_INTEGER;

    return candidates.reduce((best, candidate) => (familyRank(candidate) < familyRank(best) ? candidate : best));
  }

  getTypeOfApproval(): TaskType[] {
    //
    // Return all system task types that are approvals
    //
    const approvalTypes = [SystemTaskTypes.Approval, SystemTaskTypes.ProcessingActivityApproval];

    return this.items.filter((i) => approvalTypes.some((stt) => i.systemTaskType === stt));
  }

  getSystemTaskType(taskTypeId: number | undefined): SystemTaskTypes | undefined {
    if (!taskTypeId) return undefined;
    const taskType = this.get(taskTypeId);
    if (taskType && taskType.systemTaskType) {
      return taskType.systemTaskType;
    } else {
      return undefined;
    }
  }

  setCurrentUserLanguage(user: CurrentUser) {
    //set the translations for the current user
    this.items.forEach((i) => {
      if (user.language.code) {
        i.transIdx = i.getTransIdx(user.language.code, UserLanguage.getFallBack());
        if (i.transIdx === -1 && i.trans.length > 0) {
          i.transIdx = 0;
        }
        if (i.transIdx >= 0) {
          i.name = i.trans[i.transIdx].name;
          i.description = i.trans[i.transIdx].description;
        }
      }
    });
  }

  // A structural change to a form that is already in use does not amend the head: it forks the family
  // into a new form version with a new id. The base update matches on that id alone, so it silently did
  // nothing and the cache kept serving the previous head - leaving every get() of the new one a miss,
  // which is what left the form tab of a task without its name. Follow the family to the new head.
  update(taskType: TaskType) {
    if (this.has(taskType.taskTypeId) || !taskType.taskTypeFamilyId) {
      super.update(taskType);

      return;
    }

    const index = this.items.findIndex((i) => i.taskTypeFamilyId === taskType.taskTypeFamilyId);
    if (index < 0) return;

    this.itemSet.delete(this.items[index].taskTypeId);
    this.items[index] = taskType;
    this.itemSet.set(taskType.taskTypeId, taskType);
  }

  // The server fills this cache with active heads only, so an archived or deleted family has to leave
  // it. The head in here can be a later version than the row the caller holds (a fork moved the family
  // on), so a family match removes it as well.
  remove(taskType: TaskType) {
    if (this.has(taskType.taskTypeId) || !taskType.taskTypeFamilyId) {
      super.remove(taskType);

      return;
    }

    const head = this.items.find((i) => i.taskTypeFamilyId === taskType.taskTypeFamilyId);
    if (head) super.remove(head);
  }

  // What a producer (a template, an event) hands a new task is a form, not the version it happened to
  // be bound to when it was read: a fork moves the family on and the server repoints its producers, so
  // a producer read before that carries a version that is no longer current. This cache holds one row
  // per family - the head - which is what that binding has to resolve to. A family that is not in here
  // (hidden, archived) keeps whatever the producer remembers.
  getHead(taskTypeId: number | undefined, taskTypeFamilyId: number | undefined): TaskType | undefined {
    if (taskTypeFamilyId) {
      const head = this.items.find((i) => i.taskTypeFamilyId === taskTypeFamilyId);
      if (head) return head;
    }

    return this.has(taskTypeId) ? this.get(taskTypeId) : undefined;
  }

  async getItems(refresh: boolean = false): Promise<TaskType[]> {
    if (this.isLoading && this.taskTypePromise) {
      return await this.taskTypePromise;
    }

    if (this.items.length === 0 || refresh) {
      if (this.appContext && !this.isLoading) {
        try {
          const accessToken = await this.appContext.getAccessToken(apiRequest.scopes);
          this.taskTypePromise = apiGetTaskTypes(accessToken);
          this.isLoading = true;
          this.items = await this.taskTypePromise;
        } catch (err) {
          this.appContext.setError(err);
        } finally {
          this.isLoading = false;
          this.taskTypePromise = undefined;
        }
      } else if (!this.appContext) {
        this.items = [];
      }
    }

    return this.items;
  }

  clone(): TaskTypeCache {
    const taskStateCache: TaskTypeCache = new TaskTypeCache();
    taskStateCache.items = this.items?.map((u) => u.clone());

    return taskStateCache;
  }
}
